/**
 * Field registry — Phase E1 polish.
 *
 * Maps each completeness-rule entity_type to the list of fields admins can
 * mark required, grouped by the screen/tab where the user fills them in.
 * Used by the "Add completeness rule" modal to render a grouped <Select>
 * instead of a free-text input.
 *
 * Synthetic pseudo-fields (starting with `_`) are evaluated server-side
 * via custom handlers — see app/services/completeness.py.
 *
 * Keep this in sync with:
 *   - the model fields (backend/app/models/*.py)
 *   - the form layout in the corresponding *DetailPage.tsx
 *   - the seed in migrations/0010_completeness_rules.py
 */
import type { CompletenessEntityType } from './types';

export interface FieldOption {
  value: string;        // the literal field name as written to completeness_rules
  label: string;        // human-readable, e.g. "Investor nature (mifid_investor_nature)"
  short: string;        // the readable name alone, e.g. "Investor nature" (§3j)
}

export interface FieldGroup {
  group: string;        // group heading, e.g. "KYC tab"
  items: FieldOption[];
}

function fmt(field: string, label: string): FieldOption {
  return { value: field, label: `${label} (${field})`, short: label };
}

const AGREEMENT_FIELDS: FieldGroup[] = [
  {
    group: 'KYC tab — Investor identity',
    items: [
      fmt('mifid_investor_nature', 'Investor nature'),
      fmt('mifid_residence',       'Residence'),
      fmt('mifir_id',              'MIFIR identifier (NCI/LEI value)'),
      fmt('mifir_id_type',         'MIFIR id type (NCI or LEI)'),
    ],
  },
  {
    group: 'KYC tab — KYC review',
    items: [
      fmt('kyc_last_date', 'Last KYC date'),
      fmt('kyc_next_date', 'Next KYC date'),
    ],
  },
  {
    group: 'KYC tab — IAA history',
    items: [
      fmt('_has_iaa_initial', 'Original IAA row exists (synthetic)'),
    ],
  },
  {
    group: 'MiFID tab',
    items: [
      fmt('mifid_suitability_score',     'Suitability score'),
      fmt('strategic_allocation',        'Strategic allocation'),
      fmt('mifid_suitability_last_date', 'Last suitability date'),
      fmt('mifid_suitability_next_date', 'Next suitability date'),
    ],
  },
  {
    group: 'AML tab',
    items: [
      fmt('aml_risk',      'AML risk'),
      fmt('aml_last_date', 'Last AML date'),
      fmt('aml_next_date', 'Next AML date'),
    ],
  },
];

// The Person groups follow the Person page's tabs, in their order and under their names
// (DF4 + Pedro 2026-10-13: "PEP and USP are part of Identity … grouped accordingly
// everywhere"). Keep in step with FIELD_TABS in components/MissingFields.tsx.
const PERSON_FIELDS: FieldGroup[] = [
  {
    group: 'Identity tab',
    items: [
      fmt('display_name', 'Display name'),
      // P16b (migration 0105): the client record's name parts — natural-only.
      fmt('given_names', 'Given names'),
      fmt('surnames', 'Surnames'),
      fmt('id_type',            'ID type'),
      fmt('id_number',          'ID number'),
      fmt('id_issuing_country', 'ID issuing country'),
      fmt('id_issue_date',      'ID issue date'),
      fmt('id_expiry_date',     'ID expiry date'),
      fmt('pep_status',         'PEP status (individuals only)'),
      fmt('us_person',          'US person (FATCA)'),
    ],
  },
  {
    group: 'Corporate tab — legal-only',
    items: [
      fmt('legal_form',             'Legal form'),
      fmt('lei',                    'LEI'),
      fmt('nipc',                   'NIPC'),
      fmt('incorporation_country',  'Incorporation country'),
      fmt('incorporation_date',     'Incorporation date'),
      fmt('regulatory_authority',   'Regulatory authority'),
      // A seeded rule (rules_seed.sql) requires it; it was missing here (2026-10-10).
      fmt('tin',                    'TIN / Tax ID'),
      // P16b (migration 0105): the client record's company fields.
      fmt('corporate_purpose',               'Corporate purpose'),
      fmt('cae_code',                        'CAE code'),
      fmt('listed_company',                  'Listed company'),
      fmt('high_risk_countries_declaration', 'High-risk countries declaration'),
      fmt('ownership_structure',             'Ownership structure'),
    ],
  },
  {
    group: 'Birth tab — natural-only',
    items: [
      fmt('gender',                'Gender'),
      fmt('birth_date',            'Date of birth'),
      fmt('birth_place_country',   'Birth-place country'),
      fmt('nationality_1',         'Primary nationality'),
    ],
  },
  {
    group: 'Contact tab',
    items: [
      fmt('email',     'E-mail'),
      fmt('mobile_num','Mobile number'),
    ],
  },
  {
    group: 'Family tab — natural-only',
    items: [
      fmt('marital_status', 'Marital status'),
      fmt('children_count', 'Number of children'),
    ],
  },
  {
    group: 'Fiscal tab — natural-only',
    items: [
      fmt('tin_1',            'TIN / NIF'),
      fmt('fiscal_country_1', 'Fiscal country'),
    ],
  },
  {
    group: 'Profession tab — natural-only',
    items: [
      fmt('professional_status', 'Professional status'),
      fmt('profession',          'Profession'),
      fmt('employer',            'Employer'),
    ],
  },
  {
    group: 'KYC tab',
    items: [
      fmt('kyc_last_date', 'Last KYC date'),
      fmt('kyc_next_date', 'Next KYC date'),
      // P16b (migration 0105): the client record's financial blocks — natural-only.
      fmt('source_of_wealth',    'Source of wealth'),
      fmt('expected_flows',      'Expected flows'),
      fmt('tax_obligations_met', 'Tax obligations met'),
      fmt('financial_situation', 'Financial situation'),
    ],
  },
];

const ACCOUNT_FIELDS: FieldGroup[] = [
  {
    group: 'Identity',
    items: [
      // The bank is chosen from the Banks list (migration 0112). Its SWIFT/BIC, address
      // and contacts are the bank's (Admin → Banks), no longer account fields.
      fmt('bank_id',       'A bank is chosen'),
      fmt('internal_num',  'Bank internal number'),
      fmt('rto_approved',  'RTO approved'),
    ],
  },
  {
    group: 'Bank',
    items: [
      fmt('iban',         'IBAN'),
    ],
  },
];

// H1.6: pseudo-fields for INSURANCE_POLICY rules. The "_has_*" entries
// are computed at evaluation time on the backend (not persisted columns
// on the policy itself); the registry exposes them so admins can see /
// pick them in the "Add completeness rule" modal alongside the regular
// fields.
const INSURANCE_POLICY_FIELDS: FieldGroup[] = [
  {
    group: 'Linked entities (computed)',
    items: [
      fmt('_has_account',       'At least one Account'),
      fmt('_has_policy_holder', 'At least one Policy Holder'),
      fmt('_has_life_insured',  'At least one Life Insured'),
      fmt('_has_beneficiary',   'At least one Beneficiary'),
    ],
  },
  {
    group: 'Identity',
    items: [
      fmt('insurer',       'Insurer'),
      fmt('policy_number', 'Policy number'),
      fmt('fund_type',     'Fund type'),
      fmt('fund_number',   'Fund number'),
    ],
  },
];

const REGISTRY: Record<CompletenessEntityType, FieldGroup[]> = {
  AGREEMENT:        AGREEMENT_FIELDS,
  PERSON:           PERSON_FIELDS,
  ACCOUNT:          ACCOUNT_FIELDS,
  INSURANCE_POLICY: INSURANCE_POLICY_FIELDS,
};

/** Flat list of {group, value, label} ready for a Mantine grouped Select. */
export function fieldOptionsForSelect(et: CompletenessEntityType) {
  return REGISTRY[et].map((g) => ({
    group: g.group,
    items: g.items,
  }));
}

/** Find the human-friendly label for a field name; falls back to the
 *  raw name when the field is not in the registry (e.g. a custom rule). */
export function fieldHumanLabel(et: CompletenessEntityType, fieldName: string): string {
  for (const grp of REGISTRY[et]) {
    for (const item of grp.items) {
      if (item.value === fieldName) return item.label;
    }
  }
  return fieldName;
}

// Readable names for the computed checks (the `_`-prefixed rule fields evaluated
// server-side) that are not offered in the registry above.
const COMPUTED_SHORT: Record<string, string> = {
  _has_iaa_initial: 'Original IAA on record',
  _iaa_signed_dates_complete: 'IAA signing dates',
  _allocation_within_suitability: 'Allocation within suitability',
  _billing_identities_complete: 'Billing details',
  _tin_1_valid_pt_nif: 'Valid NIF',
  _iban_valid_iso13616: 'Valid IBAN',
  _has_account: 'Linked account',
  _has_policy_holder: 'Policy holder',
  _has_life_insured: 'Life insured',
  _has_beneficiary: 'Beneficiary',
};

/** The readable name alone (no raw field name), for screens a user reads — e.g. the
 *  incomplete-record banner (§3j). Falls back to the field name with spaces. */
export function fieldShortLabel(et: CompletenessEntityType, fieldName: string): string {
  if (COMPUTED_SHORT[fieldName]) return COMPUTED_SHORT[fieldName];
  for (const grp of REGISTRY[et]) {
    for (const item of grp.items) {
      if (item.value === fieldName) return item.short;
    }
  }
  const words = fieldName.replace(/^_+/, '').replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}


// ─── Condition dimensions ─────────────────────────────────────────────────
// For each entity type, the list of attributes that make sense as a rule
// condition (`applies_when` keys), each with the closed set of legal
// values. Used by the "Add completeness rule" modal so admins pick from
// dropdowns instead of typing free text (avoids case mismatches like
// "natural" vs "NATURAL").

export interface ConditionDimension {
  key: string;
  label: string;
  values: { value: string; label: string }[];
}

const AGREEMENT_DIMENSIONS: ConditionDimension[] = [
  {
    key: 'client_type', label: 'Client type',
    values: [
      { value: 'Particular', label: 'Particular' },
      { value: 'Empresa',    label: 'Empresa' },
    ],
  },
  {
    key: 'aml_risk', label: 'AML risk',
    values: [
      { value: 'Normal',  label: 'Normal' },
      { value: 'Elevado', label: 'Elevado' },
    ],
  },
  {
    // Migration 0028 (#273) — frontend rename from Activo/Inactivo to the
    // shared LifecycleStatus enum (Active/Inactive/Closed). User-facing
    // labels remain English now; PT-localisation can be reintroduced via
    // an i18n layer when needed.
    key: 'status', label: 'Status',
    values: [
      { value: 'Active',   label: 'Active' },
      { value: 'Inactive', label: 'Inactive' },
      { value: 'Closed',   label: 'Closed' },
    ],
  },
  {
    key: 'service_type', label: 'Service',
    values: [
      { value: 'CIIF', label: 'CIIF' },
      { value: 'CEC',  label: 'CEC' },
    ],
  },
];

const PERSON_DIMENSIONS: ConditionDimension[] = [
  {
    key: 'kind', label: 'Kind',
    values: [
      { value: 'NATURAL', label: 'NATURAL' },
      { value: 'LEGAL',   label: 'LEGAL' },
    ],
  },
  {
    // P16b (2026-10-10): a computed condition — the person holds this role on at least
    // one agreement (services/completeness.py `_role`).
    key: '_role', label: 'Role on an agreement',
    values: [
      { value: 'HOLDER',         label: 'Holder' },
      { value: 'BENEFICIARY',    label: 'Beneficiary' },
      { value: 'REPRESENTATIVE', label: 'Representative' },
    ],
  },
];

const ACCOUNT_DIMENSIONS: ConditionDimension[] = [
  {
    key: 'rto_approved', label: 'RTO approved',
    values: [
      { value: 'true',  label: 'true' },
      { value: 'false', label: 'false' },
    ],
  },
];

// H1.6: no condition dimensions on INSURANCE_POLICY for now — every
// policy is treated uniformly by the seeded _has_* rules. If a future
// rule needs to apply only to (e.g.) policies of a particular insurer
// or fund_type, add a dimension here.
const INSURANCE_POLICY_DIMENSIONS: ConditionDimension[] = [];

const CONDITION_REGISTRY: Record<CompletenessEntityType, ConditionDimension[]> = {
  AGREEMENT:        AGREEMENT_DIMENSIONS,
  PERSON:           PERSON_DIMENSIONS,
  ACCOUNT:          ACCOUNT_DIMENSIONS,
  INSURANCE_POLICY: INSURANCE_POLICY_DIMENSIONS,
};

export function conditionDimensionsFor(et: CompletenessEntityType): ConditionDimension[] {
  return CONDITION_REGISTRY[et];
}

/** Lookup helper: return the legal values for a (entity_type, key) pair,
 *  or null if no such dimension is registered. */
export function conditionValuesFor(
  et: CompletenessEntityType, key: string,
): { value: string; label: string }[] | null {
  const dim = CONDITION_REGISTRY[et].find((d) => d.key === key);
  return dim ? dim.values : null;
}
